import { useEffect, useRef, useState } from "react";

/**
 * Первый экран главной: короткий урок на доске, который проигрывается сам.
 *
 * Без слов показывает то, что текстом объясняется долго: учитель ставит
 * задание, ученица решает на той же доске, учитель проверяет — и всё
 * остаётся на доске. Это не видео и не картинка, а обычная вёрстка:
 * весит ничего, резкая на любом экране.
 *
 * Крутится, только пока сцена в кадре: за экраном таймеры не тикают.
 * Тем, кто просит браузер меньше двигать, показываем последний кадр.
 */

/** Длина круга, мс. */
const LOOP_MS = 12000;

/** Когда что появляется, мс от начала круга. */
const AT = {
  sticky: 200,
  typeFrom: 500,
  equation: 2400,
  student: 4300,
  discriminant: 4700,
  roots: 6100,
  check: 7400,
  comment: 7800,
  saved: 9000,
  fadeOut: 11300,
} as const;

const TASK = "Реши уравнение";
const TYPE_STEP_MS = 110;

function formatTimer(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function HomeLessonScene() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [t, setT] = useState(0);
  const [timer, setTimer] = useState(5 * 60);
  const [still, setStill] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setStill(true);
      setT(AT.saved + 500);
      setTimer(4 * 60 + 12);
      return;
    }
    let visible = true;
    let start = performance.now();
    let pausedAt = 0;
    const io =
      typeof IntersectionObserver !== "undefined" && rootRef.current
        ? new IntersectionObserver(([e]) => {
            const now = performance.now();
            if (!e) return;
            if (e.isIntersecting && !visible) start += now - pausedAt;
            if (!e.isIntersecting && visible) pausedAt = now;
            visible = e.isIntersecting;
          })
        : null;
    if (io && rootRef.current) io.observe(rootRef.current);
    const tick = window.setInterval(() => {
      if (!visible || document.hidden) return;
      setT((performance.now() - start) % LOOP_MS);
    }, 80);
    const clock = window.setInterval(() => {
      if (!visible || document.hidden) return;
      setTimer((s) => (s <= 1 ? 5 * 60 : s - 1));
    }, 1000);
    return () => {
      io?.disconnect();
      window.clearInterval(tick);
      window.clearInterval(clock);
    };
  }, []);

  const on = (at: number) => t >= at && t < AT.fadeOut;
  const typed = TASK.slice(0, Math.max(0, Math.floor((t - AT.typeFrom) / TYPE_STEP_MS)));
  const typing = t >= AT.typeFrom && typed.length < TASK.length;
  // Курсор учителя: сначала у стикера, потом пишет уравнение, потом проверяет.
  const teacherPos = on(AT.check) ? "check" : on(AT.equation) ? "equation" : "sticky";

  return (
    <div
      ref={rootRef}
      className={`wb-air-scene${still ? " is-still" : ""}`}
      role="img"
      aria-label="Пример урока на доске: учитель ставит задание, ученица решает уравнение на той же доске, учитель проверяет."
    >
      <div className="wb-air-scene__board" aria-hidden="true">
        <ul className="wb-air-scene__rail">
          <li className="is-active">
            <svg viewBox="0 0 24 24"><path d="M4 20l4-1 11-11-3-3L5 16l-1 4z" /></svg>
          </li>
          <li>
            <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="14" rx="2" /></svg>
          </li>
          <li>
            <svg viewBox="0 0 24 24"><path d="M5 6h14M12 6v13" /></svg>
          </li>
          <li>
            <svg viewBox="0 0 24 24"><path d="M5 4h14v11l-5 5H5z" /></svg>
          </li>
        </ul>

        <div className={`wb-air-scene__sticky${on(AT.sticky) ? " is-on" : ""}`}>
          <span className="wb-air-scene__sticky-label">Задание</span>
          <span className="wb-air-scene__sticky-text">
            {still ? TASK : typed}
            {typing ? <i className="wb-air-scene__caret" /> : null}
          </span>
        </div>

        <p className={`wb-air-scene__ink wb-air-scene__ink--eq${on(AT.equation) ? " is-on" : ""}`}>
          x² − 5x + 6 = 0
        </p>
        <p className={`wb-air-scene__ink wb-air-scene__ink--d${on(AT.discriminant) ? " is-on" : ""}`}>
          D = 25 − 24 = 1
        </p>
        <p className={`wb-air-scene__ink wb-air-scene__ink--roots${on(AT.roots) ? " is-on" : ""}`}>
          x₁ = 2,&nbsp; x₂ = 3
        </p>

        <svg
          className={`wb-air-scene__check${on(AT.check) ? " is-on" : ""}`}
          viewBox="0 0 60 44"
        >
          <path d="M4 24 L22 40 L56 4" />
        </svg>

        <div className={`wb-air-scene__cursor wb-air-scene__cursor--teacher is-at-${teacherPos}${on(AT.sticky) ? " is-on" : ""}`}>
          <svg viewBox="0 0 20 20"><path d="M2 2l15 6-6 2-2 6z" /></svg>
          <span>Мария Петровна</span>
        </div>
        <div
          className={`wb-air-scene__cursor wb-air-scene__cursor--student${on(AT.student) ? " is-on" : ""}${
            on(AT.roots) ? " is-at-roots" : ""
          }`}
        >
          <svg viewBox="0 0 20 20"><path d="M2 2l15 6-6 2-2 6z" /></svg>
          <span>Аня</span>
        </div>

        <div className="wb-air-scene__timer">
          <span>Таймер</span>
          <b>{formatTimer(timer)}</b>
        </div>

        <div className={`wb-air-scene__comment${on(AT.comment) ? " is-on" : ""}`}>
          <span className="wb-air-scene__avatar">М</span>
          <p>
            <b>Мария Петровна</b>
            Верно! Теперь попробуй через теорему Виета.
          </p>
        </div>

        <div className={`wb-air-scene__saved${on(AT.saved) ? " is-on" : ""}`}>
          <svg viewBox="0 0 20 20"><path d="M4 10.5l4 4 8-9" /></svg>
          Доска сохранится — Аня вернётся к ней дома
        </div>
      </div>
    </div>
  );
}
